import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { useWorkMeta } from "@/lib/workMeta";
import { label } from "@/lib/constants";
import { StatusBadge, SeverityBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, ClipboardCheck, TrendingDown, TrendingUp, AlertTriangle } from "lucide-react";

const emptyAreas = (meta) =>
  (meta?.inspection_areas || []).map((a) => ({ key: a.key, condition: "not_checked", notes: "", photos: [] }));

export default function Inspections() {
  const { user } = useAuth();
  const meta = useWorkMeta();
  const canInspect = can(user, "inspection:create");
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ property_id: "", notes: "", missing_items: "", safety_concerns: "" });
  const [areas, setAreas] = useState([]);
  const [history, setHistory] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => {
    api.get("/inspections").then(({ data }) => setRows(data));
    api.get("/properties").then(({ data }) => setProperties(data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (meta && areas.length === 0) setAreas(emptyAreas(meta));
  }, [meta, areas.length]);

  const setArea = (key, patch) =>
    setAreas((list) => list.map((a) => (a.key === key ? { ...a, ...patch } : a)));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inspections", { ...form, areas, submit: true });
      toast.success("Inspection submitted");
      setOpen(false);
      setForm({ property_id: "", notes: "", missing_items: "", safety_concerns: "" });
      setAreas(emptyAreas(meta));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit inspection");
    }
    setBusy(false);
  };

  const openHistory = async (pid) => {
    setHistory({ loading: true });
    const { data } = await api.get(`/properties/${pid}/condition-history`);
    setHistory(data);
  };

  const scoreTone = (score) =>
    score === null || score === undefined
      ? "text-muted-foreground"
      : score >= 4.5
        ? "text-emerald-600"
        : score >= 3.5
          ? "text-royal"
          : "text-red-600";

  return (
    <div className="space-y-6" data-testid="inspections-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Asset stewardship</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Inspections</h1>
          <p className="text-muted-foreground mt-3 text-sm md:text-base">
            Documented condition of every property over time — so problems are caught, not inherited.
          </p>
        </div>
        {canInspect && (
          <Button
            data-testid="new-inspection-btn"
            onClick={() => setOpen(true)}
            className="bg-royal hover:bg-royal-deep text-white gap-2 h-11"
          >
            <Plus className="h-4 w-4" /> New inspection
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="condition-history-buttons">
        {properties.map((p) => (
          <button
            key={p._id}
            data-testid={`history-${p._id}`}
            onClick={() => openHistory(p._id)}
            className="rounded-full border border-border bg-white px-4 min-h-[40px] text-xs font-medium text-royal hover:border-royal/40 transition-colors duration-200"
          >
            {p.name} history
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-royal/25 bg-white p-10 text-center">
          <ClipboardCheck className="h-7 w-7 text-gold mx-auto" />
          <p className="font-display text-lg font-semibold text-royal-deep mt-3">No inspections yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Inspections are created automatically when a clean finishes, or start one manually.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="inspections-list">
          {rows.map((i) => (
            <button
              key={i._id}
              data-testid={`inspection-card-${i._id}`}
              onClick={() => setViewing(i)}
              className="text-left rounded-lg border border-border bg-white p-5 hover:shadow-md hover:-translate-y-[2px] transition-transform duration-200"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display font-semibold text-royal-deep truncate">{i.property_name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {i.inspector_name} · {new Date(i.submitted_at || i.created_at).toLocaleDateString()}
                  </p>
                </div>
                <StatusBadge value={i.status} testId={`inspection-status-${i._id}`} />
              </div>
              <div className="mt-4 flex items-end gap-3">
                <p className={`font-display text-3xl font-bold leading-none ${scoreTone(i.overall_score)}`}>
                  {i.overall_score ?? "—"}
                </p>
                <p className="text-xs text-muted-foreground">/ 5 condition score</p>
                {i.passed ? (
                  <span className="ml-auto text-[11px] font-semibold text-emerald-700">PASSED</span>
                ) : (
                  <span className="ml-auto text-[11px] font-semibold text-red-700">NEEDS WORK</span>
                )}
              </div>
              {(i.failing_areas || []).length > 0 && (
                <p className="mt-3 text-xs text-muted-foreground">
                  Flagged: {i.failing_areas.map((a) => label(a)).join(", ")}
                </p>
              )}
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="inspection-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">Property inspection</DialogTitle>
            <DialogDescription>
              Rate each area. Anything poor, damaged or missing fails the inspection and triggers follow-up work.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="inspection-form">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Property</Label>
              <Select value={form.property_id} onValueChange={(v) => setForm((f) => ({ ...f, property_id: v }))}>
                <SelectTrigger data-testid="inspection-property-select" className="mt-1.5 h-11">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p._id} value={p._id} data-testid={`inspection-property-option-${p._id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              {(meta?.inspection_areas || []).map((a) => {
                const current = areas.find((x) => x.key === a.key) || {};
                return (
                  <div key={a.key} className="rounded-md border border-border p-3" data-testid={`area-${a.key}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="text-sm font-medium text-royal-deep flex-1 min-w-[140px]">{a.label}</p>
                      <Select
                        value={current.condition || "not_checked"}
                        onValueChange={(v) => setArea(a.key, { condition: v })}
                      >
                        <SelectTrigger data-testid={`area-condition-${a.key}`} className="w-[160px] h-10 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(meta?.conditions || []).map((c) => (
                            <SelectItem key={c.value} value={c.value} data-testid={`condition-${a.key}-${c.value}`}>
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      data-testid={`area-notes-${a.key}`}
                      value={current.notes || ""}
                      onChange={(e) => setArea(a.key, { notes: e.target.value })}
                      placeholder="Notes (optional)"
                      className="mt-2 h-10 text-sm"
                    />
                  </div>
                );
              })}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Missing items</Label>
              <Input data-testid="inspection-missing-input" value={form.missing_items}
                onChange={(e) => setForm((f) => ({ ...f, missing_items: e.target.value }))} className="mt-1.5 h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Safety concerns</Label>
              <Input data-testid="inspection-safety-input" value={form.safety_concerns}
                onChange={(e) => setForm((f) => ({ ...f, safety_concerns: e.target.value }))} className="mt-1.5 h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Overall notes</Label>
              <Textarea data-testid="inspection-notes-input" rows={3} value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1.5" />
            </div>

            <Button
              data-testid="submit-inspection-btn"
              type="submit"
              disabled={busy || !form.property_id}
              className="w-full h-12 bg-royal hover:bg-royal-deep text-white font-semibold"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit inspection"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="inspection-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">{viewing?.property_name}</DialogTitle>
            <DialogDescription>
              Inspected by {viewing?.inspector_name} ·{" "}
              {viewing?.submitted_at && new Date(viewing.submitted_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <p className={`font-display text-4xl font-bold ${scoreTone(viewing?.overall_score)}`}>
                {viewing?.overall_score ?? "—"}
              </p>
              <div>
                <p className="text-sm font-semibold text-royal-deep">
                  {viewing?.passed ? "Passed inspection" : "Failed — follow-up work raised"}
                </p>
                <p className="text-xs text-muted-foreground">{viewing?.areas_checked} areas checked</p>
              </div>
            </div>
            {(viewing?.areas || [])
              .filter((a) => a.condition !== "not_checked")
              .map((a) => (
                <div key={a.key} className="flex items-start gap-3 rounded-md border border-border p-3">
                  <SeverityBadge
                    value={a.condition === "good" ? "low" : a.condition === "fair" ? "medium" : "urgent"}
                    testId={`detail-area-${a.key}`}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-royal-deep">{label(a.key)}</p>
                    <p className="text-xs text-muted-foreground">
                      {label(a.condition)}
                      {a.notes ? ` — ${a.notes}` : ""}
                    </p>
                  </div>
                </div>
              ))}
            {viewing?.notes && <p className="text-sm text-muted-foreground">{viewing.notes}</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!history} onOpenChange={(v) => !v && setHistory(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="condition-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">Condition history</DialogTitle>
            <DialogDescription>Scores over time and problems that keep coming back.</DialogDescription>
          </DialogHeader>
          {history?.loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-royal" />
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  ["Latest", history?.latest_score ?? "—"],
                  ["Average", history?.average_score ?? "—"],
                  ["Inspections", history?.inspections ?? 0],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-md border border-border p-3 text-center">
                    <p className="font-display text-2xl font-bold text-royal-deep">{v}</p>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{k}</p>
                  </div>
                ))}
              </div>
              {history?.trend !== null && history?.trend !== undefined && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="condition-trend">
                  {history.trend >= 0 ? (
                    <TrendingUp className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-600" />
                  )}
                  Condition has moved {history.trend >= 0 ? "up" : "down"} {Math.abs(history.trend)} points since the
                  first inspection.
                </p>
              )}
              {(history?.recurring_problems || []).length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="recurring-problems">
                  <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                    <AlertTriangle className="h-4 w-4" /> Recurring problems
                  </p>
                  <ul className="mt-2 space-y-1">
                    {history.recurring_problems.map((r) => (
                      <li key={r.area} className="text-xs text-amber-900">
                        {r.label} — flagged {r.occurrences} times (last {new Date(r.last_seen).toLocaleDateString()})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="space-y-2">
                {(history?.timeline || []).map((t) => (
                  <div key={t.inspection_id} className="flex items-center gap-3 rounded-md border border-border p-3">
                    <p className={`font-display text-lg font-bold ${scoreTone(t.score)}`}>{t.score ?? "—"}</p>
                    <div className="min-w-0">
                      <p className="text-sm text-royal-deep">{new Date(t.date).toLocaleDateString()}</p>
                      <p className="text-xs text-muted-foreground">{t.inspector}</p>
                    </div>
                    <span className="ml-auto text-[11px] font-semibold">
                      {t.passed ? (
                        <span className="text-emerald-700">PASSED</span>
                      ) : (
                        <span className="text-red-700">FAILED</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
